import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { DrillColorBy, PlanningMode, TimeScale } from '../../planning.types';
import { MonthOption } from '../../utils/column-generator';

/**
 * Barre de contrôles du planning, sur une seule ligne : sous-onglet, coloration,
 * mois, granularité, navigation de période et rapport. Purement presentational.
 */
@Component({
  selector: 'app-planning-controls',
  standalone: true,
  templateUrl: './planning-controls.component.html',
  styleUrls: ['./planning-controls.component.scss'],
  imports: [FormsModule, TranslateModule, SelectComponent, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningControlsComponent {
  readonly mode         = input<PlanningMode>('drilling');
  readonly colorBy      = input<DrillColorBy>('rig');
  readonly timeScale    = input<TimeScale>('week');
  readonly monthOptions = input.required<MonthOption[]>();
  readonly monthId      = input.required<string>();
  /** Clé de traduction du premier critère de couleur (« Rig » en forage). */
  readonly primaryColorByLabel = input<string>('PLANNING.RIG');
  readonly reportDisabled      = input<boolean>(true);

  readonly modeChange      = output<PlanningMode>();
  readonly colorByChange   = output<DrillColorBy>();
  readonly timeScaleChange = output<TimeScale>();
  readonly monthChange     = output<string>();
  readonly todayClick      = output<void>();
  /** -1 pour la période précédente, +1 pour la suivante. */
  readonly navigate        = output<-1 | 1>();
  readonly reportClick     = output<void>();

  private readonly translate = inject(TranslateService);

  protected readonly colorByOptions = computed<SelectOption[]>(() => [
    { id: 'rig',      label: this.translate.instant(this.primaryColorByLabel()) },
    { id: 'operator', label: this.translate.instant('PLANNING.OPERATOR') },
  ]);

  protected readonly timeScaleOptions = computed<SelectOption[]>(() => [
    { id: 'week',  label: this.translate.instant('PLANNING.WEEK') },
    { id: 'month', label: this.translate.instant('PLANNING.MONTH') },
  ]);

  protected onColorBy(option: SelectOption): void {
    this.colorByChange.emit(option.id as DrillColorBy);
  }

  protected onTimeScale(option: SelectOption): void {
    this.timeScaleChange.emit(option.id as TimeScale);
  }

  protected onMonth(option: SelectOption): void {
    this.monthChange.emit(String(option.id));
  }
}
